import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Link } from 'lucide-react';
import type { PlanType } from '../../types';
import OptionToggle from '../common/OptionToggle';

interface ResourceItem {
  id: string;
  name: string;
  color?: string;
  isDefault?: boolean;
}

interface BudgetItem {
  id: string;
  name: string;
  /** IDs des comptes ciblés (null = tous les comptes) */
  bankAccountIds: string[] | null;
  /** 'personal' ou 'shared' */
  type: 'personal' | 'shared';
}

interface EnvelopeItem {
  id: string;
  name: string;
  type: 'personal' | 'shared';
}

interface DowngradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selection: {
    keepPersonalAccountIds: string[];
    keepSharedAccountIds: string[];
    keepBudgetIds: string[];
    keepEnvelopeIds: string[];
  }) => void;
  personalAccounts: ResourceItem[];
  sharedAccounts: ResourceItem[];
  budgets: BudgetItem[];
  envelopes: EnvelopeItem[];
  maxPersonalAccounts: number;
  maxSharedAccounts: number;
  maxBudgets: number;
  maxEnvelopes: number;
  currentMaxPersonalAccounts?: number;
  currentMaxSharedAccounts?: number;
  currentMaxBudgets?: number;
  currentMaxEnvelopes?: number;
  isLoading?: boolean;
  targetPlan?: PlanType;
}

export default function DowngradeModal({
  isOpen,
  onClose,
  onConfirm,
  personalAccounts,
  sharedAccounts,
  budgets,
  envelopes,
  maxPersonalAccounts,
  maxSharedAccounts,
  maxBudgets,
  maxEnvelopes,
  currentMaxPersonalAccounts = Infinity,
  currentMaxSharedAccounts = Infinity,
  currentMaxBudgets = Infinity,
  currentMaxEnvelopes = Infinity,
  isLoading = false,
  targetPlan = 'free',
}: DowngradeModalProps) {
  const { t } = useTranslation('subscription');
  const { t: tc } = useTranslation('common');

  // Initialiser avec les comptes par défaut pré-sélectionnés
  const [selectedPersonal, setSelectedPersonal] = useState<string[]>(() => {
    const def = personalAccounts.find(a => a.isDefault);
    return def ? [def.id] : personalAccounts.length > 0 ? [personalAccounts[0].id] : [];
  });
  const [selectedShared, setSelectedShared] = useState<string[]>(() => {
    const def = sharedAccounts.find(a => a.isDefault);
    return def ? [def.id] : sharedAccounts.length > 0 ? [sharedAccounts[0].id] : [];
  });
  const [selectedBudgets, setSelectedBudgets] = useState<string[]>([]);
  const [selectedEnvelopes, setSelectedEnvelopes] = useState<string[]>([]);

  // Ne demander la sélection que si le plan cible est plus restrictif que le plan actuel
  const needsPersonalSelection = personalAccounts.length > maxPersonalAccounts && maxPersonalAccounts < currentMaxPersonalAccounts;
  const needsBudgetSelection = budgets.length > maxBudgets && maxBudgets < currentMaxBudgets;
  const needsEnvelopeSelection = envelopes.length > maxEnvelopes && maxEnvelopes < currentMaxEnvelopes;

  // Déterminer si un budget est disponible en fonction des comptes sélectionnés
  const isBudgetAvailableFn = (budget: BudgetItem): boolean => {
    if (budget.type === 'shared') return true;
    if (!budget.bankAccountIds) return true;
    if (!needsPersonalSelection) return true;
    return budget.bankAccountIds.some(id => selectedPersonal.includes(id));
  };

  // Retirer les budgets devenus indisponibles de la sélection quand les comptes changent
  useEffect(() => {
    if (!needsBudgetSelection) return;
    setSelectedBudgets(prev => {
      const filtered = prev.filter(id => {
        const budget = budgets.find(b => b.id === id);
        return budget && isBudgetAvailableFn(budget);
      });
      return filtered.length !== prev.length ? filtered : prev;
    });
  }, [selectedPersonal]);

  if (!isOpen) return null;

  const needsSharedSelection = sharedAccounts.length > maxSharedAccounts && maxSharedAccounts < currentMaxSharedAccounts;
  const needsAnySelection = needsPersonalSelection || needsSharedSelection || needsBudgetSelection || needsEnvelopeSelection;

  const toggleItem = (
    id: string,
    selected: string[],
    setSelected: (v: string[]) => void,
    max: number,
  ) => {
    if (max === 1) {
      setSelected([id]);
    } else {
      setSelected(
        selected.includes(id)
          ? selected.filter(x => x !== id)
          : selected.length < max
            ? [...selected, id]
            : selected,
      );
    }
  };

  const canConfirm =
    (!needsPersonalSelection || selectedPersonal.length === maxPersonalAccounts) &&
    (!needsSharedSelection || selectedShared.length === maxSharedAccounts) &&
    (!needsBudgetSelection || selectedBudgets.length === maxBudgets) &&
    (!needsEnvelopeSelection || selectedEnvelopes.length === maxEnvelopes);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              {t('downgrade.title')}
            </h2>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            {needsAnySelection
              ? (targetPlan === 'free'
                  ? t('downgrade.message')
                  : t('downgrade.messagePlanChange', { plan: t(`plans.${targetPlan}`) }))
              : (targetPlan === 'free'
                  ? t('downgrade.confirmCancel')
                  : t('downgrade.confirmPlanChange', { plan: t(`plans.${targetPlan}`) }))
            }
          </p>

          {/* Sélection comptes personnels */}
          {needsPersonalSelection && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                {t('downgrade.keepPersonalAccounts')} — {t('downgrade.remaining', { count: maxPersonalAccounts - selectedPersonal.length })}
              </h3>
              <div className="space-y-2">
                {personalAccounts.map((account) => (
                  <OptionToggle
                    key={account.id}
                    selected={selectedPersonal.includes(account.id)}
                    onToggle={() => toggleItem(account.id, selectedPersonal, setSelectedPersonal, maxPersonalAccounts)}
                    color={account.color || '#6B7280'}
                  >
                    <span className="flex items-center gap-2">
                      <span className="truncate">{account.name}</span>
                      {account.isDefault && (
                        <span className="text-xs ml-auto flex-shrink-0" style={{ color: 'var(--text-3)' }}>{tc('default')}</span>
                      )}
                    </span>
                  </OptionToggle>
                ))}
              </div>
            </div>
          )}

          {/* Sélection comptes partagés */}
          {needsSharedSelection && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                {t('downgrade.keepSharedAccounts')} — {t('downgrade.remaining', { count: maxSharedAccounts - selectedShared.length })}
              </h3>
              <div className="space-y-2">
                {sharedAccounts.map((account) => (
                  <OptionToggle
                    key={account.id}
                    selected={selectedShared.includes(account.id)}
                    onToggle={() => toggleItem(account.id, selectedShared, setSelectedShared, maxSharedAccounts)}
                    color={account.color || '#6B7280'}
                  >
                    <span className="flex items-center gap-2">
                      <span className="truncate">{account.name}</span>
                      {account.isDefault && (
                        <span className="text-xs ml-auto flex-shrink-0" style={{ color: 'var(--text-3)' }}>{tc('default')}</span>
                      )}
                    </span>
                  </OptionToggle>
                ))}
              </div>
            </div>
          )}

          {/* Sélection budgets */}
          {needsBudgetSelection && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                {t('downgrade.keepBudgets')} — {t('downgrade.remaining', { count: maxBudgets - selectedBudgets.length })}
              </h3>
              <div className="space-y-2">
                {budgets.map((budget) => {
                  const available = isBudgetAvailableFn(budget);
                  const isLinked = budget.type === 'personal' && budget.bankAccountIds && budget.bankAccountIds.length > 0;
                  return (
                    <OptionToggle
                      key={budget.id}
                      selected={selectedBudgets.includes(budget.id)}
                      disabled={!available}
                      onToggle={() => available && toggleItem(budget.id, selectedBudgets, setSelectedBudgets, maxBudgets)}
                    >
                      <span className="flex items-center gap-2">
                        <span className="truncate">{budget.name}</span>
                        {isLinked && !available && (
                          <span className="ml-auto flex items-center gap-1 text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>
                            <Link className="w-3 h-3" />
                            {t('downgrade.linkedToAccount')}
                          </span>
                        )}
                      </span>
                    </OptionToggle>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sélection enveloppes */}
          {needsEnvelopeSelection && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                {t('downgrade.keepEnvelopes')} — {t('downgrade.remaining', { count: maxEnvelopes - selectedEnvelopes.length })}
              </h3>
              <div className="space-y-2">
                {envelopes.map((envelope) => (
                  <OptionToggle
                    key={envelope.id}
                    selected={selectedEnvelopes.includes(envelope.id)}
                    onToggle={() => toggleItem(envelope.id, selectedEnvelopes, setSelectedEnvelopes, maxEnvelopes)}
                  >
                    {envelope.name}
                  </OptionToggle>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              {tc('cancel')}
            </button>
            <button
              onClick={() => onConfirm({
                keepPersonalAccountIds: needsPersonalSelection ? selectedPersonal : personalAccounts.map(a => a.id),
                keepSharedAccountIds: needsSharedSelection ? selectedShared : sharedAccounts.map(a => a.id),
                keepBudgetIds: needsBudgetSelection ? selectedBudgets : budgets.map(b => b.id),
                keepEnvelopeIds: needsEnvelopeSelection ? selectedEnvelopes : envelopes.map(e => e.id),
              })}
              disabled={!canConfirm || isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {isLoading ? tc('loading') : (targetPlan === 'free' ? t('downgrade.confirmAndCancel') : t('downgrade.confirmAndChange'))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
